import { useEffect } from 'react';

/*
  Freezes the header of every wide PrimeReact DataTable against the page
  scroll, without giving the table its own internal vertical scrollbar
  and without breaking its horizontal scroll — fully automatically, for
  every table in the app, with zero per-page setup.

  "Wide" covers two cases, both detected here, neither requiring any
  className or prop on the page's own <DataTable>:
    - Tables using PrimeReact's `scrollable` prop (`.p-datatable-scrollable`)
      — an explicit, pre-existing signal the author already had to add
      for a different reason (frozen columns, capped height, etc).
    - Plain tables that simply render wider than the box they're given —
      detected every frame by comparing the real <table> element's
      rendered width against its .p-datatable root's width. The moment a
      table's columns don't fit, it's flipped into the same "contained
      horizontal scroll" mode (WIDE_CLASS below) and back out again if a
      resize makes it fit once more.

  Why this can't be plain CSS `position: sticky`: a wide table's wrapper
  needs `overflow-x: auto` for its horizontal scrollbar, and the browser
  forces `overflow-y` to behave the same way once any axis is non-visible
  — that makes the wrapper itself the sticky containing block instead of
  the page (see App.css for the full writeup). There is no CSS-only way
  to opt just one axis out of that, and no CSS-only way to conditionally
  apply that containment only when content actually overflows — so both
  the wide/not-wide decision and the freeze itself are done here in JS.

  For tables that stay narrower than their box, none of this JS ever
  touches them: their header freezes for free via the plain CSS
  `position: sticky` rule in App.css, already unlocked for the page.

  For wide tables, this reimplements the sticky visual effect with
  `transform: translateY()` computed from live scroll position, applied
  to the REAL thead — not a clone. That matters: several of these tables
  have live filter inputs and dropdowns inside the header row
  (filterDisplay="row"); a cloned header would duplicate those as dead,
  unfocusable copies. Transform only changes paint position, not the DOM
  tree, so React's event handlers, focus state, and controlled inputs are
  completely unaffected — and since the thead never leaves the table's
  layout flow (unlike position: fixed, which would blockify a <thead>
  and lose its column widths), it also never loses sync with the tbody's
  columns, including the table's own horizontal scroll (still handled
  natively by the browser, since header and body are the same table).
*/

const FROZEN_CLASS = 'js-frozen-scrollable-thead';
const WIDE_CLASS = 'table-scroll-x';
const WIDE_OVERFLOW_SLACK_PX = 2; // avoids flapping from sub-pixel rounding

const getNavBottomPx = (): number => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--app-fixed-header-height');
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 60;
};

// Backs the element's already-applied transform out of its measured rect, so
// "where is this naturally, ignoring my own last offset" can be answered
// with a pure read — no `style.transform = ''` write-then-remeasure step.
//
// That reset-then-remeasure approach was tried first and has a real bug:
// forcing a style write and immediately reading layout back can settle
// against a still-adjusting reflow (observed on transactions/payment-history
// — a table whose thead sits inside a `position: sticky; top: 0` shell,
// per App.css's writeup on why `.table-scroll-x` needs that). The result was
// a permanently-wrong ~14px offset with no further change to correct it.
// Deriving the natural position algebraically instead of asking the DOM to
// re-settle avoids that class of bug entirely.
const getCurrentTranslateYPx = (el: HTMLElement): number => {
    const transform = getComputedStyle(el).transform;
    if (!transform || transform === 'none') return 0;
    const match = transform.match(/^matrix\(([^)]+)\)$/);
    if (!match) return 0;
    const parts = match[1].split(',').map((v) => parseFloat(v.trim()));
    return parts.length >= 6 && Number.isFinite(parts[5]) ? parts[5] : 0;
};

const updateFrozenHeader = (thead: HTMLElement, table: HTMLElement, navBottom: number): void => {
    const paintedRect = thead.getBoundingClientRect();
    const naturalTop = paintedRect.top - getCurrentTranslateYPx(thead);
    const tableRect = table.getBoundingClientRect();

    let offset = 0;

    if (naturalTop < navBottom && tableRect.bottom > navBottom + paintedRect.height) {
        // Don't push the header past the last row once the table's tail end
        // has scrolled by — matches how native sticky stops sticking.
        const maxOffset = tableRect.bottom - paintedRect.height - naturalTop;
        offset = Math.min(navBottom - naturalTop, Math.max(maxOffset, 0));
    }

    if (offset > 0.5) {
        thead.style.transform = `translateY(${offset}px)`;
        thead.classList.add(FROZEN_CLASS);
    } else {
        thead.style.transform = '';
        thead.classList.remove(FROZEN_CLASS);
    }
};

const updateWideTableDetection = (root: HTMLElement): void => {
    const wrapper = root.querySelector<HTMLElement>('.p-datatable-wrapper');
    const tableEl = wrapper?.querySelector<HTMLTableElement>('table');
    if (!wrapper || !tableEl) return;

    const isWide = tableEl.getBoundingClientRect().width > root.getBoundingClientRect().width + WIDE_OVERFLOW_SLACK_PX;
    const wasWide = root.classList.contains(WIDE_CLASS);
    root.classList.toggle(WIDE_CLASS, isWide);

    if (wasWide && !isWide) {
        // Back to fitting its box — drop any leftover transform so plain
        // CSS sticky (already unlocked for non-wide tables) takes over
        // cleanly instead of stacking with a stale offset.
        const thead = root.querySelector<HTMLElement>('.p-datatable-thead');
        if (thead) {
            thead.style.transform = '';
            thead.classList.remove(FROZEN_CLASS);
        }
    }
};

export const useFrozenScrollableTableHeaders = (): void => {
    useEffect(() => {
        let rafId: number | null = null;

        /*
          Runs every animation frame, unconditionally — not "on scroll/resize/
          mutation". That was tried first (scroll + resize listeners, plus a
          MutationObserver for async data loads) and it has a real race: when
          a table's real data replaces a placeholder row, React can commit
          the DOM in a way that finishes settling (fonts/images included)
          *after* the last mutation this observer sees. The hook would
          compute a transform against a still-settling layout and then never
          get another trigger to correct it — a visible, permanent few-pixel
          misalignment with no further event to fix it (reproduced on
          transactions/payment-history: the header settled ~14px off after
          its 30s-polled data replaced an initial short placeholder).

          Enumerating every async thing that can move a table (images,
          webfonts, CSS transitions, polling refreshes, virtualized rows...)
          isn't tractable. Recomputing every frame sidesteps the whole
          category: at 60fps the cost is a handful of getBoundingClientRect
          reads (usually 1-2 tables per page), and requestAnimationFrame
          already pauses itself while the tab is backgrounded, so there's no
          idle cost and no drift is ever more than one frame old.
        */
        const tick = (): void => {
            const navBottom = getNavBottomPx();

            document.querySelectorAll<HTMLElement>('.p-datatable:not(.p-datatable-scrollable)').forEach(updateWideTableDetection);

            document.querySelectorAll<HTMLElement>(`.p-datatable.p-datatable-scrollable, .p-datatable.${WIDE_CLASS}`).forEach((table) => {
                const thead = table.querySelector<HTMLElement>('.p-datatable-thead');
                if (thead) updateFrozenHeader(thead, table, navBottom);
            });

            rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);

        return () => {
            if (rafId !== null) cancelAnimationFrame(rafId);
        };
    }, []);
};
